/**
 * doubao-stream.ts —— 火山引擎豆包 ASR 协议适配器
 *
 * 设计文档 3.5.2 的完整实现。这里只关心「怎么跟豆包说话」，
 * 不涉及麦克风采集（那是 audio-capture 的事）。
 *
 * 协议三件套：
 *   ① 二进制帧编解码（4 字节 header + payload size + payload）
 *   ② Gzip 压缩（首包 JSON 与音频帧都压缩）
 *   ③ 结果解析（从 JSON 响应里抠出识别文本与是否终句）
 *
 * ⚠️ 一个绕不开的事实：浏览器的 WebSocket API **不允许自定义 header**，
 * 而豆包的鉴权必须走 header。所以浏览器端不能直连，
 * 必须经本地转发服务（server/asr-proxy.mjs）注入 header。
 * 我们把 apiKey / resourceId 通过 query 传给代理，代理再填进 header。
 * 这也是整个 PoC 唯一需要后端的地方（D12）。
 *
 * 关于压缩：浏览器有 CompressionStream('gzip')，但它在所有环境下可用性不一。
 * 为了不让语音功能因为一个压缩 API 就整个不可用，这里做成「能用就用」：
 * 能压就压（compression=1），不能压就发原始数据（compression=0），
 * 服务端两种都认。
 */

import type { ASRConfig, SpeechProvider, SpeechSession } from './provider';
import { startCapture, type CaptureHandle } from './audio-capture';

/* ============================================================
   协议常量
   ============================================================ */

const PROTOCOL_VERSION = 0b0001;
const HEADER_SIZE = 0b0001; // 4 字节

const MSG_FULL_CLIENT = 0b0001; // 首包
const MSG_AUDIO_ONLY = 0b0010; // 音频包
const MSG_SERVER_RESPONSE = 0b1001;
const MSG_SERVER_ERROR = 0b1111;

const FLAG_POS_SEQ = 0b0001;
const FLAG_LAST_NO_SEQ = 0b0010;

const SERIAL_JSON = 0b0001;
const SERIAL_NONE = 0b0000;

const COMPRESS_NONE = 0b0000;
const COMPRESS_GZIP = 0b0001;

/** 一句话识别的资源 ID（新版控制台） */
export const DEFAULT_RESOURCE_ID = 'volc.seedasr.sauc.duration';

/* ============================================================
   帧构造
   ============================================================ */

interface FrameOptions {
  messageType: number;
  flags: number;
  serialization: number;
  compression: number;
  /** 可选序号（flags 带序号时写入） */
  sequence?: number;
  payload: Uint8Array;
}

/** 按协议拼一个二进制帧 */
function buildFrame(opt: FrameOptions): Uint8Array {
  const hasSeq =
    opt.flags === FLAG_POS_SEQ ||
    (opt.flags & 0b0011) === 0b0011 ||
    opt.flags === 0b0011;

  const extra = (hasSeq ? 4 : 0) + 4; // 序号 + payloadSize
  const buf = new ArrayBuffer(4 + extra + opt.payload.length);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);

  bytes[0] = (PROTOCOL_VERSION << 4) | HEADER_SIZE;
  bytes[1] = (opt.messageType << 4) | opt.flags;
  bytes[2] = (opt.serialization << 4) | opt.compression;
  bytes[3] = 0x00;

  let offset = 4;
  if (hasSeq) {
    view.setInt32(offset, opt.sequence ?? 0);
    offset += 4;
  }
  view.setUint32(offset, opt.payload.length);
  offset += 4;

  bytes.set(opt.payload, offset);
  return bytes;
}

/* ============================================================
   帧解析
   ============================================================ */

interface ParsedFrame {
  messageType: number;
  flags: number;
  compression: number;
  sequence: number;
  payload: Uint8Array;
  errorCode?: number;
  errorMessage?: string;
}

function parseFrame(data: ArrayBuffer): ParsedFrame {
  const view = new DataView(data);
  const bytes = new Uint8Array(data);

  const headerSize = bytes[0] & 0x0f;
  const messageType = bytes[1] >> 4;
  const flags = bytes[1] & 0x0f;
  const compression = bytes[2] & 0x0f;

  let offset = headerSize; // 通常 4

  // 错误帧：ErrorCode(4) + ErrorSize(4) + ErrorMessage
  if (messageType === MSG_SERVER_ERROR) {
    const code = view.getUint32(offset);
    offset += 4;
    const size = view.getUint32(offset);
    offset += 4;
    const msg = new TextDecoder().decode(bytes.slice(offset, offset + size));
    return { messageType, flags, compression, sequence: 0, payload: new Uint8Array(0), errorCode: code, errorMessage: msg };
  }

  let sequence = 0;
  // flags 含序号位时，先读 4 字节序号
  if (flags === FLAG_POS_SEQ || flags === 0b0011) {
    sequence = view.getInt32(offset);
    offset += 4;
  }

  const size = view.getUint32(offset);
  offset += 4;
  const payload = bytes.slice(offset, offset + size);

  return { messageType, flags, compression, sequence, payload };
}

/* ============================================================
   Gzip
   ============================================================ */

async function gzip(input: Uint8Array): Promise<Uint8Array | null> {
  const CS = (globalThis as unknown as { CompressionStream?: typeof CompressionStream }).CompressionStream;
  if (!CS) return null;
  try {
    const stream = new Blob([input as unknown as BlobPart]).stream().pipeThrough(new CS('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

async function gunzip(input: Uint8Array): Promise<Uint8Array> {
  const DS = (globalThis as unknown as { DecompressionStream?: typeof DecompressionStream }).DecompressionStream;
  if (!DS) return input;
  try {
    const stream = new Blob([input as unknown as BlobPart]).stream().pipeThrough(new DS('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return input;
  }
}

/* ============================================================
   会话实现
   ============================================================ */

class DoubaoSession implements SpeechSession {
  private ws: WebSocket | null = null;
  private capture: CaptureHandle | null = null;
  private partialHandler: ((text: string, isFinal: boolean) => void) | null = null;
  private errorHandler: ((e: { kind: string; message: string }) => void) | null = null;
  private finalText = '';
  private seq = 1;
  private finished = false;
  private closedResolve: ((text: string) => void) | null = null;

  constructor(private readonly cfg: ASRConfig, private readonly lang: string) {}

  onPartial(cb: (text: string, isFinal: boolean) => void): void {
    this.partialHandler = cb;
  }

  onError(cb: (e: { kind: string; message: string }) => void): void {
    this.errorHandler = cb;
  }

  private fail(kind: string, message: string): void {
    if (this.finished) return;
    this.finished = true;
    this.errorHandler?.({ kind, message });
    void this.teardown();
    this.closedResolve?.(this.finalText);
  }

  async start(): Promise<void> {
    const url = buildProxyUrl(this.cfg);

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      ws.onopen = async () => {
        try {
          await this.sendFirstPacket();
          settled = true;
          resolve();
        } catch (err) {
          reject(err);
        }
      };

      ws.onmessage = (e) => void this.handleMessage(e.data as ArrayBuffer);
      ws.onerror = () => {
        if (!settled) {
          settled = true;
          reject(new Error('连接语音服务失败。检查本地转发服务是否启动、代理地址是否正确。'));
        } else {
          this.fail('NetworkError', '语音服务连接中断。');
        }
      };
      ws.onclose = (ev) => {
        if (!settled) {
          settled = true;
          reject(new Error(translateCloseCode(ev.code)));
          return;
        }
        // 服务端主动关闭 = 识别结束
        if (!this.finished) {
          this.finished = true;
          this.closedResolve?.(this.finalText);
        }
      };
    });

    // WebSocket 就绪后才开麦克风 —— 顺序反了会丢前面几帧音频
    this.capture = await startCapture({
      sampleRate: 16000,
      chunkMs: 100,
      onChunk: (pcm) => void this.sendAudio(pcm),
    });
  }

  /** 首包：JSON 说明音频格式与请求参数 */
  private async sendFirstPacket(): Promise<void> {
    const payload = {
      user: { uid: deviceUid() },
      audio: { format: 'pcm', codec: 'raw', rate: 16000, bits: 16, channel: 1 },
      request: {
        model_name: 'bigmodel',
        enable_punc: true,
        enable_itn: true,
        language: this.lang === 'zh-CN' ? 'zh' : undefined,
      },
    };
    const json = new TextEncoder().encode(JSON.stringify(payload));
    const compressed = await gzip(json);

    const frame = buildFrame({
      messageType: MSG_FULL_CLIENT,
      flags: FLAG_POS_SEQ,
      serialization: SERIAL_JSON,
      compression: compressed ? COMPRESS_GZIP : COMPRESS_NONE,
      sequence: this.seq++,
      payload: compressed ?? json,
    });
    this.ws?.send(frame);
  }

  /** 音频包：每个分片压不压看 CompressionStream 在不在 */
  private async sendAudio(pcm: Int16Array): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const raw = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
    const compressed = await gzip(raw);
    const frame = buildFrame({
      messageType: MSG_AUDIO_ONLY,
      flags: FLAG_POS_SEQ,
      serialization: SERIAL_NONE,
      compression: compressed ? COMPRESS_GZIP : COMPRESS_NONE,
      sequence: this.seq++,
      payload: compressed ?? raw,
    });
    this.ws.send(frame);
  }

  /** 处理服务端来的帧 */
  private async handleMessage(data: ArrayBuffer): Promise<void> {
    let frame: ParsedFrame;
    try {
      frame = parseFrame(data);
    } catch {
      return; // 解析不了就忽略，不影响主流程
    }

    if (frame.messageType === MSG_SERVER_ERROR) {
      this.fail('ServiceError', `语音服务返回错误 ${frame.errorCode}：${frame.errorMessage ?? ''}`);
      return;
    }

    if (frame.messageType !== MSG_SERVER_RESPONSE) return;

    let payload = frame.payload;
    if (frame.compression === COMPRESS_GZIP) {
      payload = await gunzip(payload);
    }

    let obj: unknown;
    try {
      obj = JSON.parse(new TextDecoder().decode(payload));
    } catch {
      return;
    }

    const text = extractText(obj);
    if (text) {
      this.finalText = text;
      this.partialHandler?.(text, false);
    }

    // flags = 0b0011 表示这是最后一包结果
    if (frame.flags === 0b0011) {
      this.finished = true;
      this.partialHandler?.(this.finalText, true);
      this.closedResolve?.(this.finalText);
      void this.teardown();
    }
  }

  async stop(): Promise<string> {
    // 先发负包，告诉服务端"音频到此为止"
    if (this.ws?.readyState === WebSocket.OPEN) {
      const last = buildFrame({
        messageType: MSG_AUDIO_ONLY,
        flags: FLAG_LAST_NO_SEQ,
        serialization: SERIAL_NONE,
        compression: COMPRESS_NONE,
        payload: new Uint8Array(0),
      });
      this.ws.send(last);

      // 等最终结果，最多 3 秒（用户松手后不该无限等）
      const text = await new Promise<string>((resolve) => {
        this.closedResolve = resolve;
        setTimeout(() => resolve(this.finalText), 3000);
      });
      await this.teardown();
      return text;
    }
    await this.teardown();
    return this.finalText;
  }

  async cancel(): Promise<void> {
    this.finished = true;
    await this.teardown();
  }

  private async teardown(): Promise<void> {
    await this.capture?.stop().catch(() => {});
    this.capture = null;
    try {
      this.ws?.close();
    } catch {
      /* 已关闭 */
    }
    this.ws = null;
  }
}

/* ============================================================
   工具
   ============================================================ */

/** 从响应 JSON 里抠识别文本。豆包的结构是 payload_msg.result.text，兼容 utterances */
function extractText(obj: unknown): string {
  const o = obj as { payload_msg?: { result?: { text?: string; utterances?: { text?: string; definite?: boolean }[] } } };
  const r = o?.payload_msg?.result;
  if (!r) return '';
  if (typeof r.text === 'string' && r.text) return r.text;
  if (Array.isArray(r.utterances)) {
    return r.utterances.map((u) => u.text ?? '').join('');
  }
  return '';
}

/** 拼转发服务地址，把鉴权参数放进 query（代理取出后填进 header） */
function buildProxyUrl(cfg: ASRConfig): string {
  const base = cfg.proxyUrl.replace(/\/+$/, '');
  const qs = new URLSearchParams({
    apiKey: cfg.apiKey,
    resourceId: cfg.resourceId || DEFAULT_RESOURCE_ID,
  });
  return `${base}/?${qs.toString()}`;
}

function translateCloseCode(code: number): string {
  switch (code) {
    case 4001:
      return '语音服务缺少 API Key。请到设置页「语音识别」里填上。';
    case 4502:
      return '语音服务的上游连接出错，可能是 Key 无效或额度用尽。';
    default:
      return `语音服务连接被关闭（code ${code}）。检查本地转发服务是否启动。`;
  }
}

function deviceUid(): string {
  const KEY = 'lx.voice.uid';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `lx-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return 'lx-anon';
  }
}

/* ============================================================
   Provider 实现
   ============================================================ */

export const doubaoProvider: SpeechProvider = {
  name: 'doubao',

  isAvailable(cfg: ASRConfig): boolean {
    if (!cfg.apiKey || !cfg.proxyUrl) return false;
    if (typeof window !== 'undefined' && 'isSecureContext' in window && !window.isSecureContext) {
      return false;
    }
    return typeof WebSocket !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
  },

  unavailableReason(cfg: ASRConfig): string {
    if (!cfg.apiKey) return '语音服务还没配置：去设置页填上 API Key。';
    if (!cfg.proxyUrl) return '语音服务还没配置：缺少本地转发地址。';
    if (typeof window !== 'undefined' && 'isSecureContext' in window && !window.isSecureContext) {
      return '语音输入只能在 HTTPS 或 localhost 下使用。';
    }
    if (!navigator.mediaDevices?.getUserMedia) return '这个浏览器不支持麦克风采集。';
    return '语音服务当前不可用。';
  },

  async start(cfg: ASRConfig, opt?: { lang?: string }): Promise<SpeechSession> {
    const session = new DoubaoSession(cfg, opt?.lang ?? 'zh-CN');
    await session.start();
    return session;
  },
};
